import React, { useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  InputAdornment,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StoreIcon from '@material-ui/icons/Store';
import SecurityIcon from '@material-ui/icons/Security';
import SearchIcon from '@material-ui/icons/Search';
import SelectAllIcon from '@material-ui/icons/SelectAll';
import PrintIcon from '@material-ui/icons/Print';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearAllIcon from '@material-ui/icons/ClearAll';

const useStyles = makeStyles((theme) => ({
  searchBox: {
    marginBottom: theme.spacing(2)
  },
  listContainer: {
    maxHeight: 400,
    overflow: 'auto'
  },
  selectAllButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  counter: {
    color: theme.palette.primary.main,
    fontWeight: 'bold'
  }
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

export const UserPermissionsSelector = ({
  stations = [],
  companies = [],
  rules = [],
  printers = [],
  selectedStations = [],
  selectedRules = [],
  selectedPrinter = null,
  onStationsChange,
  onRulesChange,
  onPrinterChange,
  disabled = false
}) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [stationSearch, setStationSearch] = useState('');
  const [ruleSearch, setRuleSearch] = useState('');

  const groupedStations = useMemo(() => 
    companies.map(company => ({
      ...company,
      stations: [...(company.stations || [])]
    }))
  , [companies]);

  const handleCompanyToggle = useCallback((company) => {
    const companyStationIds = new Set(company.stations.map(s => s.id));
    const currentSelectedIds = new Set(selectedStations.map(s => s.id));
    
    const allSelected = company.stations.every(station => 
      currentSelectedIds.has(station.id)
    );
    
    let newSelection;
    if (allSelected) {
      newSelection = selectedStations.filter(s => !companyStationIds.has(s.id));
    } else {
      const toAdd = company.stations.filter(s => !currentSelectedIds.has(s.id));
      newSelection = [...selectedStations, ...toAdd];
    }
    
    onStationsChange(newSelection);
  }, [selectedStations, onStationsChange]);

const stationMatchesSearch = useCallback((company, station, search) => {
    const term = search.toLowerCase();
    return (
      (station.label || station.name || '').toLowerCase().includes(term) ||
      company.name.toLowerCase().includes(term)
    );
  }, []);

  const filteredStations = useMemo(() =>
    groupedStations.flatMap(company =>
      company.stations.filter(s => stationMatchesSearch(company, s, stationSearch))
    )
  , [groupedStations, stationSearch, stationMatchesSearch]);

  const filteredRules = useMemo(() => 
    rules.filter(rule =>
      (rule.label || rule.name || '').toLowerCase().includes(ruleSearch.toLowerCase()) ||
      (rule.description || '').toLowerCase().includes(ruleSearch.toLowerCase())
    )
  , [rules, ruleSearch]);

  const handleStationToggle = (station) => {
    const isSelected = selectedStations.some(s => s.id === station.id);
    const newSelection = isSelected
      ? selectedStations.filter(s => s.id !== station.id)
      : [...selectedStations, station];
    onStationsChange(newSelection);
  };

  const handleRuleToggle = (rule) => {
    const isSelected = selectedRules.some(r => r.id === rule.id);
    const newSelection = isSelected
      ? selectedRules.filter(r => r.id !== rule.id)
      : [...selectedRules, rule];
    onRulesChange(newSelection);
  };

  const handlePrinterToggle = (printer) => {
    const isSelected = selectedPrinter?.id === printer.id;
    onPrinterChange(isSelected ? null : printer);
  };

  const selectAllStations = () => onStationsChange(filteredStations);
  const clearAllStations = () => onStationsChange([]);
  const selectAllRules = () => onRulesChange(filteredRules);
  const clearAllRules = () => onRulesChange([]);

  return (
    <Card>
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab 
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <StoreIcon />
              Estaciones 
              <span className={classes.counter}>({selectedStations.length})</span>
            </Box>
          } 
        />
        <Tab 
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <SecurityIcon />
              Reglas 
              <span className={classes.counter}>({selectedRules.length})</span>
            </Box>
          } 
        />
        <Tab 
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <PrintIcon />
              Impresora 
              <span className={classes.counter}>({selectedPrinter ? 1 : 0})</span>
            </Box>
          } 
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <TextField
          placeholder="Buscar estaciones..."
          value={stationSearch}
          onChange={(e) => setStationSearch(e.target.value)}
          fullWidth
          className={classes.searchBox}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          disabled={disabled}
        />
        
        <Box className={classes.selectAllButtons}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={selectAllStations}
            disabled={disabled}
          >
            Todas
          </Button>
          <Button
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={clearAllStations}
            disabled={disabled}
          >
            Ninguna
          </Button>
        </Box>

        <Box className={classes.listContainer}>
          {stationSearch.trim() && groupedStations.every(company =>
            company.stations.filter(s => stationMatchesSearch(company, s, stationSearch)).length === 0
          ) && (
            <Typography color="textSecondary" align="center">Sin resultados</Typography>
          )}
          {groupedStations.map((company) => {
            const visibleStations = stationSearch.trim()
              ? company.stations.filter(s => stationMatchesSearch(company, s, stationSearch))
              : company.stations;
            if (visibleStations.length === 0) return null;
            return (
            <Accordion key={company.id}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
              >
                <div 
                  className="company-checkbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) handleCompanyToggle(company);
                  }}
                  style={{ display: 'flex', alignItems: 'center', cursor: disabled ? 'default' : 'pointer' }}
                >
                  <Checkbox
                    checked={(() => {
                      const selectedIds = new Set(selectedStations.map(s => s.id));
                      return company.stations.length > 0 && company.stations.every(station => selectedIds.has(station.id));
                    })()}
                    indeterminate={(() => {
                      const selectedIds = new Set(selectedStations.map(s => s.id));
                      const someSelected = company.stations.some(station => selectedIds.has(station.id));
                      const allSelected = company.stations.every(station => selectedIds.has(station.id));
                      return someSelected && !allSelected;
                    })()}
                    disabled={disabled}
                    color="primary"
                    style={{ marginRight: 8 }}
                  />
                  <Typography>{company.name} ({company.stations.filter(station => selectedStations.some(s => s.id === station.id)).length}/{company.stations.length})</Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <List dense style={{ width: '100%' }}>
                  {visibleStations.map((station) => (
                    <ListItem 
                      key={station.id} 
                      button 
                      onClick={() => !disabled && handleStationToggle(station)}
                      style={{ cursor: disabled ? 'default' : 'pointer', paddingLeft: 32 }}
                    >
                      <Checkbox
                        checked={selectedStations.some(s => s.id === station.id)}
                        disabled={disabled}
                        color="primary"
                        style={{ marginRight: 8 }}
                      />
                      <span>{station.name}</span>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            );
          })}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TextField
          placeholder="Buscar reglas..."
          value={ruleSearch}
          onChange={(e) => setRuleSearch(e.target.value)}
          fullWidth
          className={classes.searchBox}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          disabled={disabled}
        />
        
        <Box className={classes.selectAllButtons}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={selectAllRules}
            disabled={disabled}
          >
            Todas
          </Button>
          <Button
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={clearAllRules}
            disabled={disabled}
          >
            Ninguna
          </Button>
        </Box>

        <Box className={classes.listContainer}>
          <List dense>
            {filteredRules.map((rule) => (
              <ListItem 
                key={rule.id} 
                button 
                onClick={() => !disabled && handleRuleToggle(rule)}
                style={{ cursor: disabled ? 'default' : 'pointer' }}
              >
                <Checkbox
                  checked={selectedRules.some(r => r.id === rule.id)}
                  disabled={disabled}
                  color="primary"
                  style={{ marginRight: 8 }}
                />
                <Box display="flex" flexDirection="column" flex={1}>
                  <Typography variant="body2">{rule.label || rule.name}</Typography>
                  {rule.description && (
                    <Typography variant="caption" color="textSecondary">
                      {rule.description}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box className={classes.listContainer}>
          <List dense>
            {printers.map((printer) => (
              <ListItem 
                key={printer.id} 
                button 
                onClick={() => !disabled && handlePrinterToggle(printer)}
                style={{ cursor: disabled ? 'default' : 'pointer' }}
              >
                <Checkbox
                  checked={selectedPrinter?.id === printer.id}
                  disabled={disabled}
                  color="primary"
                  style={{ marginRight: 8 }}
                />
                <span>{printer.label || printer.name}</span>
              </ListItem>
            ))}
            {printers.length === 0 && (
              <Typography color="textSecondary" align="center">
                No hay impresoras disponibles
              </Typography>
            )}
          </List>
        </Box>
      </TabPanel>
    </Card>
  );
};